import { useState } from 'react';
import {
  Box, Typography, Paper, Chip, Button, TextField,
  IconButton, Divider, Tooltip, Checkbox, FormControlLabel,
  FormGroup, Collapse, Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import useOrganizationCountry from '../../hooks/useOrganizationCountry';
import { BackgroundCheckChargesTip, BackgroundCheckCostInfo, CostInfoGlyph, useCheckPoints } from './backgroundCheckPricing';

const PREDEFINED_PHASES = [
  { type: 'search', name_es: 'Búsqueda activa', name_en: 'Active search' },
  { type: 'evaluation', name_es: 'Evaluación', name_en: 'Evaluation' },
  { type: 'shortlist', name_es: 'Shortlist', name_en: 'Shortlist' },
  { type: 'negotiation', name_es: 'Negociación', name_en: 'Negotiation' },
];

const PREDEFINED_STAGES = [
  { type: 'new', name_es: 'Nuevo', name_en: 'New', hasModules: true },
  { type: 'screening', name_es: 'Screening', name_en: 'Screening', hasModules: true },
  { type: 'evaluation', name_es: 'Evaluaciones', name_en: 'Evaluations', hasModules: true },
  { type: 'interview_consultant', name_es: 'Entrevista consultor', name_en: 'Consultant interview', hasModules: true },
  { type: 'interview_client', name_es: 'Entrevista cliente', name_en: 'Client interview', hasModules: true },
  { type: 'shortlist', name_es: 'Shortlist', name_en: 'Shortlist', hasModules: true },
  { type: 'offer', name_es: 'Oferta', name_en: 'Offer', hasModules: false },
  { type: 'hired', name_es: 'Contratado', name_en: 'Hired', hasModules: false },
];

const AVAILABLE_MODULES = [
  { id: 'cv_filtering', label_es: 'Filtrado de CV', label_en: 'CV Filtering', repeatable: false },
  { id: 'disc_test', label_es: 'Test DISC', label_en: 'DISC Test', repeatable: false },
  { id: 'reasoning_test', label_es: 'Test de capacidades', label_en: 'Aptitude Tests', repeatable: false },
  { id: 'personal_skills', label_es: 'Habilidades personales', label_en: 'Personal Skills', repeatable: false },
  { id: 'sales_assessment', label_es: 'Sales Assessment', label_en: 'Sales Assessment', repeatable: false },
  { id: 'interview', label_es: 'Entrevista', label_en: 'Interview', repeatable: true },
  { id: 'psicolaboral', label_es: 'Evaluación Psicolaboral', label_en: 'Psycholaboral Report', repeatable: false },
  { id: 'psycholaboral_comparison', label_es: 'Ranking psicolaboral', label_en: 'Psycholaboral Ranking', repeatable: false },
  // Botón "Verificar antecedentes" (Chapi) en la columna. Una sola etapa por
  // vacante; solo organizaciones que operan en Chile (PLAN_MASIVO_PORTAL)
  { id: 'background_check', label_es: 'Verificación de antecedentes', label_en: 'Background check', repeatable: false },
  // Botón "Solicitar documentos" en la columna: estado de los documentos de la
  // etapa y solicitud masiva. Puede estar en varias etapas: todo se suma a la
  // misma lista del candidato (PLAN_SOLICITUD_DOCUMENTOS_MASIVA, 2026-10-14)
  { id: 'document_request', label_es: 'Solicitud de documentos', label_en: 'Document request', repeatable: true },
];

// Módulos permitidos por tipo de etapa. Las etapas no listadas permiten todos.
// La etapa "Nuevo" admite Filtrado de CV y Verificación de antecedentes
// (PLAN_MASIVO_PORTAL, 2026-10-03).
const MODULES_BY_STAGE_TYPE = {
  new: ['cv_filtering', 'background_check'],
};

const getModulesForStage = (stage) => {
  const allowed = MODULES_BY_STAGE_TYPE[stage?.type];
  if (!allowed) return AVAILABLE_MODULES;
  return AVAILABLE_MODULES.filter((m) => allowed.includes(m.id));
};

const AtsPipelineConfigurator = ({ pipeline, onChange }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  // Módulo "Verificación de antecedentes" solo para organizaciones que operan
  // en Chile (fuera de Chile en espera, PLAN_MASIVO_PORTAL)
  const { operatesInChile, loading: orgCountryLoading } = useOrganizationCountry();
  // Precio de la verificación de antecedentes (2026-11-01, PLAN_COBRO_ANTECEDENTES)
  const { points: checkPoints } = useCheckPoints();

  const phases = pipeline?.phases || [];
  const [expandedPhases, setExpandedPhases] = useState(
    phases.reduce((acc, p) => ({ ...acc, [p.id]: true }), {})
  );
  const [expandedModules, setExpandedModules] = useState({});
  const [customPhaseName, setCustomPhaseName] = useState('');
  const [customStageNames, setCustomStageNames] = useState({});

  const activePhaseTypes = phases.filter((p) => p.type !== 'custom').map((p) => p.type);

  // El Ranking psicolaboral depende del módulo Evaluación Psicolaboral: solo puede
  // vivir en la misma etapa o en etapas posteriores. Si el psicolaboral se quita
  // (o queda después por reordenamientos), los rankings inválidos se eliminan solos.
  const sanitizePsychoComparison = (newPhases) => {
    const flatStages = newPhases.flatMap((p) => p.stages || []);
    const psicoIndex = flatStages.findIndex((s) => s.modules?.includes('psicolaboral'));
    return newPhases.map((phase) => ({
      ...phase,
      stages: (phase.stages || []).map((stage) => {
        if (!stage.modules?.includes('psycholaboral_comparison')) return stage;
        const stageIndex = flatStages.indexOf(stage);
        const isValid = psicoIndex !== -1 && stageIndex >= psicoIndex;
        return isValid
          ? stage
          : { ...stage, modules: stage.modules.filter((m) => m !== 'psycholaboral_comparison') };
      }),
    }));
  };

  const updatePipeline = (newPhases) => {
    onChange({
      ...pipeline,
      phases: sanitizePsychoComparison(newPhases),
    });
  };

  // ── FASES ────────────────────────────────────────────────────────────────

  const handleTogglePhase = (predefined) => {
    const isActive = activePhaseTypes.includes(predefined.type);
    if (isActive) {
      updatePipeline(phases.filter((p) => p.type !== predefined.type));
    } else {
      const newPhase = {
        id: predefined.type,
        name: lang === 'en' ? predefined.name_en : predefined.name_es,
        type: predefined.type,
        order: phases.length + 1,
        stages: [],
      };
      const newPhases = [...phases, newPhase].map((p, i) => ({ ...p, order: i + 1 }));
      updatePipeline(newPhases);
      setExpandedPhases((prev) => ({ ...prev, [newPhase.id]: true }));
    }
  };

  const handleAddCustomPhase = () => {
    if (!customPhaseName.trim()) return;
    const newPhase = {
      id: uuidv4(),
      name: customPhaseName.trim(),
      type: 'custom',
      order: phases.length + 1,
      stages: [],
    };
    updatePipeline([...phases, newPhase]);
    setExpandedPhases((prev) => ({ ...prev, [newPhase.id]: true }));
    setCustomPhaseName('');
  };

  const handleRemovePhase = (phaseId) => {
    updatePipeline(
      phases.filter((p) => p.id !== phaseId).map((p, i) => ({ ...p, order: i + 1 }))
    );
  };

  const handleMovePhaseUp = (index) => {
    if (index === 0) return;
    const newPhases = [...phases];
    [newPhases[index - 1], newPhases[index]] = [newPhases[index], newPhases[index - 1]];
    updatePipeline(newPhases.map((p, i) => ({ ...p, order: i + 1 })));
  };

  const handleMovePhaseDown = (index) => {
    if (index === phases.length - 1) return;
    const newPhases = [...phases];
    [newPhases[index], newPhases[index + 1]] = [newPhases[index + 1], newPhases[index]];
    updatePipeline(newPhases.map((p, i) => ({ ...p, order: i + 1 })));
  };

  const handleToggleExpandPhase = (phaseId) => {
    setExpandedPhases((prev) => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  // ── ETAPAS ───────────────────────────────────────────────────────────────

  const getActiveStageTypesInPhase = (phaseId) => {
    const phase = phases.find((p) => p.id === phaseId);
    return (phase?.stages || []).filter((s) => s.type !== 'custom').map((s) => s.type);
  };

  const handleToggleStage = (phaseId, predefined) => {
    const activeTypes = getActiveStageTypesInPhase(phaseId);
    const isActive = activeTypes.includes(predefined.type);

    const newPhases = phases.map((phase) => {
      if (phase.id !== phaseId) return phase;

      let newStages;
      if (isActive) {
        newStages = phase.stages.filter((s) => s.type !== predefined.type);
      } else {
        const newStage = {
          id: predefined.type,
          name: lang === 'en' ? predefined.name_en : predefined.name_es,
          type: predefined.type,
          order: phase.stages.length + 1,
          hasModules: predefined.hasModules,
          modules: [],
        };
        newStages = [...phase.stages, newStage];
      }

      return {
        ...phase,
        stages: newStages.map((s, i) => ({ ...s, order: i + 1 })),
      };
    });

    updatePipeline(newPhases);
  };

  const handleAddCustomStage = (phaseId) => {
    const name = customStageNames[phaseId];
    if (!name?.trim()) return;

    const newPhases = phases.map((phase) => {
      if (phase.id !== phaseId) return phase;
      const newStage = {
        id: uuidv4(),
        name: name.trim(),
        type: 'custom',
        order: phase.stages.length + 1,
        hasModules: true,
        modules: [],
      };
      return { ...phase, stages: [...phase.stages, newStage] };
    });

    updatePipeline(newPhases);
    setCustomStageNames((prev) => ({ ...prev, [phaseId]: '' }));
  };

  const handleRemoveStage = (phaseId, stageId) => {
    const newPhases = phases.map((phase) => {
      if (phase.id !== phaseId) return phase;
      return {
        ...phase,
        stages: phase.stages
          .filter((s) => s.id !== stageId)
          .map((s, i) => ({ ...s, order: i + 1 })),
      };
    });
    updatePipeline(newPhases);
  };

  const handleMoveStageUp = (phaseId, index) => {
    if (index === 0) return;
    const newPhases = phases.map((phase) => {
      if (phase.id !== phaseId) return phase;
      const newStages = [...phase.stages];
      [newStages[index - 1], newStages[index]] = [newStages[index], newStages[index - 1]];
      return { ...phase, stages: newStages.map((s, i) => ({ ...s, order: i + 1 })) };
    });
    updatePipeline(newPhases);
  };

  const handleMoveStageDown = (phaseId, index) => {
    const phase = phases.find((p) => p.id === phaseId);
    if (!phase || index === phase.stages.length - 1) return;
    const newPhases = phases.map((p) => {
      if (p.id !== phaseId) return p;
      const newStages = [...p.stages];
      [newStages[index], newStages[index + 1]] = [newStages[index + 1], newStages[index]];
      return { ...p, stages: newStages.map((s, i) => ({ ...s, order: i + 1 })) };
    });
    updatePipeline(newPhases);
  };

  // ── MÓDULOS ───────────────────────────────────────────────────────────────

  const isModuleUsedElsewhere = (moduleId, phaseId, stageId) => {
    const module = AVAILABLE_MODULES.find((m) => m.id === moduleId);
    if (module?.repeatable) return false;
    return phases.some((phase) =>
      phase.stages.some(
        (stage) =>
          !(phase.id === phaseId && stage.id === stageId) &&
          stage.modules?.includes(moduleId)
      )
    );
  };

  // Nombre de la etapa que ya tiene el módulo (para el aviso "ya se usa en otra etapa")
  const findStageUsingModule = (moduleId, phaseId, stageId) => {
    for (const phase of phases) {
      const found = phase.stages.find(
        (stage) => !(phase.id === phaseId && stage.id === stageId) && stage.modules?.includes(moduleId)
      );
      if (found) return found.name || '';
    }
    return '';
  };

  const handleToggleModule = (phaseId, stageId, moduleId) => {
    const newPhases = phases.map((phase) => {
      if (phase.id !== phaseId) return phase;
      return {
        ...phase,
        stages: phase.stages.map((stage) => {
          if (stage.id !== stageId) return stage;
          const hasModule = stage.modules.includes(moduleId);
          const next = {
            ...stage,
            modules: hasModule
              ? stage.modules.filter((m) => m !== moduleId)
              : [...stage.modules, moduleId],
          };
          // Sin el módulo de antecedentes no queda la consulta automática
          if (moduleId === 'background_check' && hasModule) delete next.backgroundCheckAuto;
          return next;
        }),
      };
    });
    updatePipeline(newPhases);
  };

  // "Consultar automáticamente al llegar a esta etapa" (solo con el módulo
  // "Verificación de antecedentes" marcado en la etapa, 2026-10-13)
  const handleToggleBackgroundCheckAuto = (phaseId, stageId, checked) => {
    const newPhases = phases.map((phase) => {
      if (phase.id !== phaseId) return phase;
      return {
        ...phase,
        stages: phase.stages.map((stage) => (stage.id === stageId ? { ...stage, backgroundCheckAuto: checked } : stage)),
      };
    });
    updatePipeline(newPhases);
  };

  const handleToggleExpandModules = (stageId) => {
    setExpandedModules((prev) => ({ ...prev, [stageId]: !prev[stageId] }));
  };

  // Gating del Ranking psicolaboral: solo en la etapa del psicolaboral o posteriores
  const flatStages = phases.flatMap((p) => p.stages || []);
  const psicoGlobalIndex = flatStages.findIndex((s) => s.modules?.includes('psicolaboral'));

  return (
    <Box>

      {/* Catálogo de fases predefinidas */}
      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
        {t('ats.positions.wizard.pipeline.phaseCatalogTitle')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {t('ats.positions.wizard.pipeline.phaseCatalogSubtitle')}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
        {PREDEFINED_PHASES.map((predefined) => {
          const isActive = activePhaseTypes.includes(predefined.type);
          return (
            <Chip
              key={predefined.type}
              label={lang === 'en' ? predefined.name_en : predefined.name_es}
              onClick={() => handleTogglePhase(predefined)}
              variant={isActive ? 'filled' : 'outlined'}
              sx={{
                cursor: 'pointer',
                fontWeight: isActive ? 600 : 400,
                backgroundColor: isActive ? '#6A5AF9' : undefined,
                color: isActive ? '#fff' : undefined,
                borderColor: isActive ? '#6A5AF9' : undefined,
                '&:hover': {
                  backgroundColor: isActive ? '#5B4DE8' : '#F3F4F6',
                },
              }}
            />
          );
        })}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Fases configuradas */}
      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
        {t('ats.positions.wizard.pipeline.activeStagesTitle')} ({phases.length})
      </Typography>

      {phases.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ border: '1px dashed #D1D5DB', borderRadius: 2, p: 3, textAlign: 'center', mb: 3 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('ats.positions.wizard.pipeline.emptyStages')}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {phases.map((phase, phaseIndex) => (
            <Paper
              key={phase.id}
              elevation={0}
              sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}
            >
              {/* Header de la fase */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                backgroundColor: '#EDE9FE',
                cursor: 'pointer',
              }}
                onClick={() => handleToggleExpandPhase(phase.id)}
              >
                {expandedPhases[phase.id]
                  ? <FolderOpenIcon sx={{ color: '#6A5AF9', mr: 1.5, fontSize: 20 }} />
                  : <FolderIcon sx={{ color: '#6A5AF9', mr: 1.5, fontSize: 20 }} />
                }

                <Box sx={{
                  width: 22, height: 22,
                  borderRadius: '50%',
                  backgroundColor: '#6A5AF9',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  mr: 1.5,
                  flexShrink: 0,
                }}>
                  {phaseIndex + 1}
                </Box>

                <Typography variant="body2" fontWeight={700} sx={{ flexGrow: 1, color: '#4C1D95' }}>
                  {phase.name}
                </Typography>

                {phase.type === 'custom' && (
                  <Chip label={t('ats.positions.wizard.pipeline.custom')} size="small" sx={{ mr: 1 }} />
                )}

                <Chip
                  label={`${phase.stages.length} ${t('ats.positions.wizard.pipeline.stagesCount')}`}
                  size="small"
                  variant="outlined"
                  sx={{ mr: 1, borderColor: '#6A5AF9', color: '#6A5AF9' }}
                />

                <Tooltip title={t('ats.positions.wizard.pipeline.moveUp')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleMovePhaseUp(phaseIndex); }}
                      disabled={phaseIndex === 0}
                    >
                      <UpIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title={t('ats.positions.wizard.pipeline.moveDown')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleMovePhaseDown(phaseIndex); }}
                      disabled={phaseIndex === phases.length - 1}
                    >
                      <DownIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title={t('ats.positions.wizard.pipeline.removeStage')}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => { e.stopPropagation(); handleRemovePhase(phase.id); }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Contenido de la fase */}
              <Collapse in={!!expandedPhases[phase.id]}>
                <Box sx={{ p: 2 }}>

                  {/* Catálogo de etapas predefinidas para esta fase */}
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                    {t('ats.positions.wizard.pipeline.stageCatalogTitle')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                    {PREDEFINED_STAGES.map((predefined) => {
                      const activeTypes = getActiveStageTypesInPhase(phase.id);
                      const isActive = activeTypes.includes(predefined.type);
                      // Deshabilitar si ya está en otra fase
                      const usedInOtherPhase = phases.some(
                        (p) => p.id !== phase.id &&
                          p.stages.some((s) => s.type === predefined.type)
                      );
                      return (
                        <Tooltip
                          key={predefined.type}
                          title={usedInOtherPhase
                            ? t('ats.positions.wizard.pipeline.stageUsedElsewhere')
                            : ''
                          }
                        >
                          <span>
                            <Chip
                              label={lang === 'en' ? predefined.name_en : predefined.name_es}
                              size="small"
                              onClick={() => !usedInOtherPhase && handleToggleStage(phase.id, predefined)}
                              disabled={usedInOtherPhase && !isActive}
                              variant={isActive ? 'filled' : 'outlined'}
                              sx={{
                                cursor: usedInOtherPhase ? 'default' : 'pointer',
                                fontWeight: isActive ? 600 : 400,
                                backgroundColor: isActive ? '#6A5AF9' : undefined,
                                color: isActive ? '#fff' : undefined,
                                borderColor: isActive ? '#6A5AF9' : undefined,
                                '&:hover': {
                                  backgroundColor: isActive
                                    ? '#5B4DE8'
                                    : usedInOtherPhase ? undefined : '#F3F4F6',
                                },
                              }}
                            />
                          </span>
                        </Tooltip>
                      );
                    })}
                  </Box>

                  {/* Etapas activas en esta fase */}
                  {phase.stages.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                      {phase.stages.map((stage, stageIndex) => (
                        <Paper
                          key={stage.id}
                          elevation={0}
                          sx={{ border: '1px solid #E5E7EB', borderRadius: 1.5, overflow: 'hidden' }}
                        >
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 1,
                            backgroundColor: '#F9FAFB',
                          }}>

                            {/* Indicador de sub-etapa */}
                            <Typography sx={{ color: '#9CA3AF', mr: 1, fontSize: '0.75rem' }}>└─</Typography>

                            {/* Número */}
                            <Box sx={{
                              width: 20, height: 20,
                              borderRadius: '50%',
                              backgroundColor: '#8B5CF6',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              mr: 1.5,
                              flexShrink: 0,
                            }}>
                              {stageIndex + 1}
                            </Box>

                            <Typography variant="body2" fontWeight={500} sx={{ flexGrow: 1 }}>
                              {stage.name}
                            </Typography>

                            {stage.type === 'custom' && (
                              <Chip
                                label={t('ats.positions.wizard.pipeline.custom')}
                                size="small"
                                sx={{ mr: 0.5, height: 18, fontSize: '0.65rem' }}
                              />
                            )}

                            {stage.hasModules && stage.modules.length > 0 && (
                              <Chip
                                label={`${stage.modules.length} ${t('ats.positions.wizard.pipeline.modules')}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ mr: 0.5, height: 18, fontSize: '0.65rem' }}
                              />
                            )}

                            {stage.hasModules && (
                              <Tooltip title={t('ats.positions.wizard.pipeline.expandModules')}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleExpandModules(stage.id)}
                                >
                                  <SettingsIcon
                                    sx={{
                                      fontSize: 16,
                                      color: expandedModules[stage.id] ? '#6A5AF9' : 'inherit',
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}

                            <Tooltip title={t('ats.positions.wizard.pipeline.moveUp')}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveStageUp(phase.id, stageIndex)}
                                  disabled={stageIndex === 0}
                                >
                                  <UpIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title={t('ats.positions.wizard.pipeline.moveDown')}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveStageDown(phase.id, stageIndex)}
                                  disabled={stageIndex === phase.stages.length - 1}
                                >
                                  <DownIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title={t('ats.positions.wizard.pipeline.removeStage')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveStage(phase.id, stage.id)}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>

                          </Box>

                          {/* Módulos */}
                          {stage.hasModules && (
                            <Collapse in={!!expandedModules[stage.id]}>
                              <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #E5E7EB' }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                                  {t('ats.positions.wizard.pipeline.modulesTitle')}
                                </Typography>
                                <FormGroup row>
                                  {getModulesForStage(stage).map((module) => {
                                    const usedElsewhere = isModuleUsedElsewhere(module.id, phase.id, stage.id);
                                    // El Ranking psicolaboral solo se habilita en la etapa
                                    // del psicolaboral o posteriores
                                    const isPsychoComparison = module.id === 'psycholaboral_comparison';
                                    const psychoComparisonBlocked = isPsychoComparison
                                      && (psicoGlobalIndex === -1 || flatStages.indexOf(stage) < psicoGlobalIndex);
                                    // Verificación de antecedentes: solo en Chile (si ya
                                    // estaba marcada, se puede desmarcar)
                                    const backgroundCheckBlocked = module.id === 'background_check'
                                      && !stage.modules.includes(module.id)
                                      && (orgCountryLoading || !operatesInChile);
                                    const isDisabled = usedElsewhere || psychoComparisonBlocked || backgroundCheckBlocked;
                                    const tooltipTitle = usedElsewhere
                                      ? t('ats.positions.wizard.pipeline.moduleUsedElsewhere', { stage: findStageUsingModule(module.id, phase.id, stage.id) })
                                      : backgroundCheckBlocked
                                        ? (orgCountryLoading ? '' : t('ats.positions.wizard.pipeline.backgroundCheckChileOnly'))
                                        : psychoComparisonBlocked
                                          ? (psicoGlobalIndex === -1
                                            ? t('ats.modules.psychoComparisonNoPsico')
                                            : t('ats.modules.psychoComparisonAfterPsico'))
                                          : isPsychoComparison
                                            ? t('ats.modules.psychoComparisonTooltip')
                                            : module.id === 'background_check'
                                              ? <BackgroundCheckChargesTip intro={t('ats.backgroundCheckPricing.moduleIntro')} />
                                              : '';
                                    return (
                                      <FormControlLabel
                                        key={module.id}
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={stage.modules.includes(module.id)}
                                            onChange={() => {
                                              if (!isDisabled) handleToggleModule(phase.id, stage.id, module.id);
                                            }}
                                            disabled={isDisabled}
                                            sx={{ '&.Mui-checked': { color: '#6A5AF9' } }}
                                          />
                                        }
                                        label={
                                          <Tooltip
                                            title={tooltipTitle}
                                            placement="top"
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{ color: isDisabled ? 'text.disabled' : 'inherit' }}
                                            >
                                              {lang === 'en' ? module.label_en : module.label_es}
                                              {module.id === 'background_check' && !isDisabled && <CostInfoGlyph />}
                                            </Typography>
                                          </Tooltip>
                                        }
                                      />
                                    );
                                  })}
                                </FormGroup>
                                {/* Consulta automática de antecedentes al llegar a la etapa */}
                                {stage.modules.includes('background_check') && (
                                  <Box sx={{ mt: 1, p: 1, backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 1 }}>
                                    <FormControlLabel
                                      control={(
                                        <Switch
                                          size="small"
                                          checked={Boolean(stage.backgroundCheckAuto)}
                                          onChange={(e) => handleToggleBackgroundCheckAuto(phase.id, stage.id, e.target.checked)}
                                          disabled={orgCountryLoading || !operatesInChile}
                                        />
                                      )}
                                      label={(
                                        <Typography variant="caption" fontWeight={600}>
                                          {t('ats.positions.wizard.pipeline.backgroundCheckAuto')}
                                          <BackgroundCheckCostInfo auto />
                                        </Typography>
                                      )}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {t('ats.positions.wizard.pipeline.backgroundCheckAutoHint', { points: checkPoints })}
                                    </Typography>
                                  </Box>
                                )}
                                {stage.modules.includes('psicolaboral') && (
                                  <Box sx={{ mt: 1, p: 1, backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {t('ats.modules.psicolaboralRecommendation')}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          )}

                        </Paper>
                      ))}
                    </Box>
                  )}

                  {/* Agregar etapa custom en esta fase */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <TextField
                      size="small"
                      placeholder={t('ats.positions.wizard.pipeline.customStagePlaceholder')}
                      value={customStageNames[phase.id] || ''}
                      onChange={(e) => setCustomStageNames((prev) => ({
                        ...prev,
                        [phase.id]: e.target.value,
                      }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustomStage(phase.id); }}
                      sx={{ flexGrow: 1 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddCustomStage(phase.id)}
                      disabled={!customStageNames[phase.id]?.trim()}
                      sx={{
                        borderColor: '#6A5AF9',
                        color: '#6A5AF9',
                        flexShrink: 0,
                        '&:hover': { borderColor: '#5B4DE8', backgroundColor: '#F5F3FF' },
                      }}
                    >
                      {t('ats.positions.wizard.pipeline.addStage')}
                    </Button>
                  </Box>

                </Box>
              </Collapse>

            </Paper>
          ))}
        </Box>
      )}

      {/* Agregar fase custom */}
      <Divider sx={{ mb: 3 }} />
      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
        {t('ats.positions.wizard.pipeline.addCustomPhaseTitle')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          placeholder={t('ats.positions.wizard.pipeline.customPhasePlaceholder')}
          value={customPhaseName}
          onChange={(e) => setCustomPhaseName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustomPhase(); }}
          sx={{ flexGrow: 1 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCustomPhase}
          disabled={!customPhaseName.trim()}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, flexShrink: 0 }}
        >
          {t('ats.positions.wizard.pipeline.addPhase')}
        </Button>
      </Box>

    </Box>
  );
};

export default AtsPipelineConfigurator;